import type { BasketResponseItem } from "@onestore/api/basket"
import type { DomainCheck } from "@onestore/api/domainSearch"
import type {
  DomainBundlePricing,
  PeriodName,
  PlanPeriodNumericId,
  Price,
  ProductAlias,
} from "@onestore/api/types"
import type { HTTP } from "@onestore/onestore-store-common"
import type { ProductPrice } from "@gatsby-plugin-bonus/lib/bonus-definitions"
import type { ParentPromotionPeriod } from "@gatsby-plugin-definitions/fragments/CloudBluePeriod"
import type { NormalizedBonusResource } from "@gatsby-plugin-definitions/lib/normalizers"
import type { DomainSearch } from "@gatsby-plugin-domain-search/types"
import type { MarkOptional, MarkRequired } from "ts-essentials"
import type { BonusProduct } from "~/store/bonus/types"
import type { FormErrors } from "../../../lib/api/forms"
import type { PeriodInfoPeriod } from "../../../lib/core/period-info"

export type BonusPeriod = MarkRequired<PeriodInfoPeriod, "resource_data"> & {
  id: number
  trial: boolean
  price: ProductPrice
  parent_promotions: ParentPromotionPeriod[]
  plan_id: number
}

export type BonusResource = {
  id: number
  basketItemId?: number
  maxQuantity: number
  quantity: number
  basketQuantity: number
  minQuantity: number
  includedValue: number
}

export type BonusResourceWithBasketItem = MarkRequired<
  BonusResource,
  "basketItemId"
>
export type ResourceId = number

export type BonusProductParameter = {
  id: string
  name: string
  description: string
  required: boolean
  alias: string | null
  type: string
}

export function isBonusProductWithRemoteStateType(
  product: BonusProduct | BonusProductWithRemoteState
): product is BonusProductWithRemoteState {
  return product.basketItemState !== undefined
}

export type BonusProductWithRemoteState = MarkRequired<
  BonusProduct,
  "basketItemState"
>

export type InitialDomainBonusProduct = {
  alias: ProductAlias
  name: string
  planId: number | null
  advancedConfiguration: boolean
  configurationSaved: boolean
  periods: BonusPeriod[]
  hasQuantity: boolean
  minQuantity: number
  totalPrices: {
    regularPrice: Price | null
    promoPrice: Price | null
  }
  chosenPeriodId: PlanPeriodNumericId | null
  previousPeriodId: PlanPeriodNumericId | null
  defaultPeriodName: PeriodName | null
  basketItemState?: BasketResponseItem
  addedToBasket: boolean
  autoAddedToBasket: boolean
  addToBasketStatus: number
  addToBasketError: undefined | FormErrors
  changePeriodStatus: number
  changePeriodError: undefined | string
  getPriceStatus: number
  getPriceError: undefined | string
  isPriceLoading: boolean
  isAddToBasketLoading: boolean
  isChangePeriodLoading: boolean
  resourceCategories: BonusResourceCategory[]
  resourceLabels: Record<ResourceId, string>
  isChangeResourceLoading: boolean
  changeResourceStatus: number
  isValid: boolean
  removedFromBasket: boolean
  errors: Record<string, string>
  parameters: Record<string, BonusProductParameter>
  removeFromBasketStatus: HTTP.Status
  pool?: string
}

export type DomainBonusProduct = InitialDomainBonusProduct & {
  planId: number
  chosenPeriodId: PlanPeriodNumericId
  previousPeriodId: PlanPeriodNumericId
}

export type ParametersPatchData = {
  patchParameters?: {
    domain?: string
  }
}

export type BonusProductForUpdate = MarkOptional<
  Pick<
    BonusProductWithRemoteState,
    "alias" | "resources" | "addedToBasket" | "basketItemState"
  >,
  "basketItemState"
>

export type UpsellDomainsProducts = {
  [alias: string]: BonusProduct
}

export type UpsellDomains = {
  results?: DomainCheck.Result[]
  status: HTTP.Status
}

export type UpsellDefinitions = UpsellDomainsProducts

export type BonusProductUpgrade = Record<string, unknown>

export type BonusFormInstance = {
  valid: boolean
  values: Record<string, string>
}

export type BonusForm = Record<ProductAlias, BonusFormInstance>

export type DomainBoxUrlParams = {
  pool: number
  extensions: string
  limit: number
}

export type SpecialBoxDefinition = {
  alias: string
  template?: string
  parameters?: DomainBoxUrlParams
}

export type PeriodPrice = {
  regularPrice: Price
  pricePerMonth: Price | null | undefined
  promoPrice?: Price
}

export type Period = {
  id: number
  name: string
  price: PeriodPrice
  trial: boolean
  type: number
  period: number
}

export type Periods = Period[]

export type ProductResource = {
  alias?: string
  id?: number
  description?: string
  quantity: number
  minQuantity: number
  maxQuantity: number
  includedValue?: number
  basketItemId?: number
}

export type BonusProductErrors = {
  parameters: Record<string, string>
}

export type Parameter = {
  id: string
  type: string
  name: string
  alias: string | null
  description: string
  required: boolean
}

export type ProductConfigCheck = {
  id: number | string
  type?: "radio" | "checkbox"
  name: string
  primaryLabel: string
  secondaryLabel?: string
  isDisabled?: boolean
  isChecked?: boolean
  isLoading?: boolean
  value: string
}

export type PasswordValidatorItem = {
  pattern: RegExp
  label: string
}

export type Resource = {
  id: number
  alias?: string | null
  description?: string
  name?: string
  measureUnit?: string
}

export type ResourceCategory = {
  id: number
  name: string
  resources?: Resource[]
  noneOption: boolean
  description: string
  displayType: number
  sortOrder: number
  parent?: number
}

export type BonusResourceCategory = {
  id: number
  name: string
  resources: NormalizedBonusResource[]
  parent: number | null | undefined
  description: string
  noneOption: boolean
  displayType: number
  sortOrder: number
  defaultResourceId: number | null
}

export type SelectedBundle = {
  mainDomain: DomainSearch.DomainFqdnWithPlan
  childDomains: DomainSearch.DomainFqdnWithPlan[]
  pricing: DomainBundlePricing
}
